import * as React from 'react';
import { Power } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

import {
  useDisableMyApplePayRegistration,
  useMyApplePayAssociationFile,
  useUpdateMyApplePayAssociationFile,
} from '@/lib/api/apple-pay-registrations';
import { RegistrationStatusBadge } from '@/components/apple-pay/byok-ui';
import { AssociationFileCard } from '@/components/apple-pay/AssociationFileCard';
import { MerchantCertificates } from './MerchantCertificates';
import { MerchantDomains } from './MerchantDomains';
import type { MyApplePayRegistration } from './types';

/** Management view for a merchant's own BYOK Apple Pay registration. */
export function RegistrationManager({ registration }: { registration: MyApplePayRegistration }) {
  const disable = useDisableMyApplePayRegistration();

  // Association file content is only fetched once the editor is opened.
  const [fileEditorOpened, setFileEditorOpened] = React.useState(false);
  const associationFile = useMyApplePayAssociationFile(registration.id, { enabled: fileEditorOpened });
  const updateAssociationFile = useUpdateMyApplePayAssociationFile();

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="text-lg">{registration.displayName}</CardTitle>
              <p className="text-muted-foreground mt-1 font-mono text-xs">
                {registration.merchantIdentifier}
              </p>
            </div>
            <RegistrationStatusBadge status={registration.status} />
          </div>
        </CardHeader>
        <CardContent className="space-y-8">
          <AssociationFileCard
            hasFile={registration.hasAssociationFile}
            domains={registration.domains.map((d) => d.domain)}
            busy={updateAssociationFile.isPending}
            currentContent={associationFile.data?.associationFileContent}
            loadingContent={associationFile.isFetching}
            onOpenEditor={() => setFileEditorOpened(true)}
            onSave={async (content) => {
              await updateAssociationFile.mutateAsync({
                registrationId: registration.id,
                associationFileContent: content,
              });
            }}
          />
          <Separator />

          {registration.status === 'disabled' && (
            <Alert>
              <AlertTitle>Using platform Apple Pay</AlertTitle>
              <AlertDescription>
                This configuration is disabled — your checkout uses Tagada&apos;s platform Apple Pay.
                Re-verify a domain to switch back to your own certificates.
              </AlertDescription>
            </Alert>
          )}

          <MerchantCertificates registration={registration} />
          <Separator />
          <MerchantDomains registration={registration} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Revert to platform Apple Pay</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-muted-foreground text-sm">
            Stop using your own certificates and fall back to Tagada&apos;s platform Apple Pay. Your
            certificates and domains are kept — re-verify a domain to switch back.
          </p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" disabled={disable.isPending || registration.status === 'disabled'}>
                <Power className="mr-2 h-4 w-4" />
                {disable.isPending ? 'Reverting…' : 'Revert to platform Apple Pay'}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Revert to platform Apple Pay?</AlertDialogTitle>
                <AlertDialogDescription>
                  Your domains will stop using your own certificates and fall back to Tagada&apos;s platform
                  Apple Pay configuration. You can switch back anytime by re-verifying a domain.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => disable.mutate({ registrationId: registration.id })}
                  disabled={disable.isPending}
                >
                  Revert
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  );
}
